import type {
  DirectorEducation,
  DirectorEmployment,
  DirectorProfile,
} from "./types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UnknownRecord = any;

const record = (value: unknown): UnknownRecord =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};
const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;
/** Coerce API string/number fields (e.g. mobileNumber: 99444888888). */
const textish = (value: unknown): string | null => {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return text(value);
};
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const bool = (value: unknown): boolean => Boolean(value);
const strings = (value: unknown): string[] =>
  list(value).map(text).filter(Boolean) as string[];

/**
 * Unwrap Portal `getDirectorApolloProfile` envelopes:
 *   { apolloPerson: { person, waterfall } }
 *   { data: { apolloPerson: { person } } }
 *   { person }  (legacy / test)
 */
function extractPersonAndMeta(payload: unknown): {
  person: UnknownRecord;
  waterfall: UnknownRecord;
  top: UnknownRecord;
} {
  const root = record(payload);
  const top = record(root.data ?? root);
  const apolloPerson = record(top.apolloPerson ?? top.apollo_person);
  const person = record(
    apolloPerson.person ??
      top.person ??
      (text(top.id) || text(top.name) || text(top.first_name) ? top : {})
  );
  const waterfall = record(
    apolloPerson.waterfall ?? top.waterfall ?? root.waterfall
  );
  return { person, waterfall, top };
}

export function normalizeDirectorApolloPayload(
  payload: unknown
): DirectorProfile | null {
  const { person, waterfall, top } = extractPersonAndMeta(payload);
  if (!text(person.id) && !text(person.name) && !text(person.first_name)) {
    return null;
  }

  const contact = record(person.contact);
  const organization = record(person.organization);

  const phoneEntry = record(list(contact.phone_numbers)[0]);
  const phoneFromContact =
    text(phoneEntry.sanitized_number) ??
    text(phoneEntry.raw_number) ??
    text(contact.sanitized_phone);

  const employmentHistory: DirectorEmployment[] = list(
    person.employment_history
  )
    .map(record)
    .map((job) => {
      const company =
        text(job.organization_name) ??
        text(job.company) ??
        text(record(job.organization).name);
      if (!company) return null;
      return {
        id: text(job.id) ?? text(job._id) ?? text(job.key),
        company,
        title: text(job.title) ?? "Role not listed",
        startDate: text(job.start_date),
        endDate: text(job.end_date),
        current: bool(job.current),
        logoUrl: text(job.logo_url) ?? text(record(job.organization).logo_url),
      };
    })
    .filter(Boolean) as DirectorEmployment[];

  const education: DirectorEducation[] = list(person.education)
    .map(record)
    .map((item) => ({
      school:
        text(item.school) ?? text(item.organization_name) ?? "School not listed",
      degree: text(item.degree),
      field: text(item.field) ?? text(item.major),
      startDate: text(item.start_date),
      endDate: text(item.end_date),
    }));

  const currentJob = employmentHistory.find((job) => job.current);

  const joinedName = [text(person.first_name), text(person.last_name)]
    .filter(Boolean)
    .join(" ");
  const fullName =
    text(person.name) ?? text(top.name) ?? (joinedName || "Unknown");

  const waterfallStatus = text(waterfall.status)?.toLowerCase() ?? null;
  const phonePending =
    !phoneFromContact &&
    (waterfallStatus === "accepted" ||
      waterfallStatus === "pending" ||
      waterfallStatus === "processing");

  return {
    apolloPersonId: text(person.id),
    din: text(top.din) ?? text(person.din),
    fullName,
    firstName: text(person.first_name),
    lastName: text(person.last_name),
    headline: text(person.headline),
    currentTitle: text(person.title) ?? text(contact.title) ?? currentJob?.title ?? null,
    currentCompany:
      text(organization.name) ??
      text(contact.organization_name) ??
      currentJob?.company ??
      null,
    companyDomain:
      text(organization.primary_domain) ??
      text(organization.website_url)
        ?.replace(/^https?:\/\//, "")
        .replace(/^www\./, "")
        .split("/")[0] ??
      null,
    photoUrl: text(person.photo_url) ?? text(contact.photo_url),
    // API top-level email / mobileNumber first; else Apollo person profile.
    email:
      textish(top.email) ??
      textish(person.email) ??
      textish(contact.email) ??
      null,
    emailStatus: text(person.email_status) ?? text(contact.email_status),
    phone:
      textish(top.mobileNumber) ??
      textish(top.mobile_number) ??
      textish(top.mobile) ??
      textish(top.phone) ??
      phoneFromContact ??
      textish(person.phone) ??
      textish(contact.sanitized_phone),
    phoneStatus:
      textish(top.mobileNumber) ||
      textish(top.mobile_number) ||
      textish(top.mobile) ||
      textish(top.phone) ||
      phoneFromContact
        ? text(phoneEntry.status) ?? "available"
        : phonePending
          ? "pending"
          : null,
    linkedinUrl: text(person.linkedin_url) ?? text(contact.linkedin_url),
    city: text(person.city) ?? text(contact.city),
    state: text(person.state) ?? text(contact.state),
    country: text(person.country) ?? text(contact.country),
    seniority: text(person.seniority),
    departments: strings(person.departments),
    overview:
      text(person.overview) ??
      text(organization.short_description) ??
      text(organization.seo_description) ??
      text(person.headline),
    matchConfidence: text(person.match_confidence),
    employmentHistory,
    education,
    enrichedAt: new Date().toISOString(),
    raw: payload,
  };
}
